import { useCallback, useMemo, useRef, useState } from 'react';
import {
    Stack,
    IDropdownOption,
    Dialog,
    DialogFooter,
    PrimaryButton,
    Toggle,
    ScrollablePane,
    TooltipHost,
    DirectionalHint,
    IconButton,
    IButtonStyles,
} from '@fluentui/react';
import { IntegrationEventType, MicrosoftEntraIdIntegration } from '../../../../../../api/generated/data-contracts';
import { useQuery } from '@tanstack/react-query';
import { disableAllSpellchecksAndAutoCompleteProp } from '../../../../../../helpers/killEvent';
import produce from 'immer';
import { ApiCalls } from '../../../../../../api/api';
import { INTEGRATIONS_MICROSOFT_ENTRAID_GROUPS } from '../../../../../../constants/queryKeys';
import { IntegrationCardHandle } from '../IntegrationCardHandle';
import { IntegrationCard } from '../IntegrationCard';
import { DataverseResourcesPane } from './Panes/DataverseResourcesPane';
import { FilterDropdown } from '../../../../../../_components/FilterDropdown/FilterDropdown';
import { useIntegrations } from '../../../../../../hooks/queries/integrations/useIntegrations';
import { useId } from '@fluentui/react-hooks';
import { IsDataverseIntegration } from '../../IntegrationTypeGeneric';

export const MicrosoftEntraIdLayout = ({
    integration,
    setIntegration,
    isLoading,
}: {
    integration: MicrosoftEntraIdIntegration;
    setIntegration: React.Dispatch<React.SetStateAction<MicrosoftEntraIdIntegration>>;
    isLoading: boolean;
}) => {
    const { enabled, includeManager, groupName, syncUsersToDataverse } = integration.configuration;
    const { integrations } = useIntegrations();
    const isAnyDataverseResourceEnabled = useMemo(
        () =>
            integrations.some(
                integration =>
                    IsDataverseIntegration(integration) && integration.configuration.enabled && integration.subscribedEvents.includes('ResourcesInbound'),
            ),
        [integrations],
    );
    const toggleEnableEvent = useCallback(
        (event: any, checked?: boolean) => {
            setIntegration(integration => {
                return {
                    ...integration,
                    configuration: {
                        ...integration.configuration,
                        enabled: checked ?? !enabled,
                    },
                };
            });
        },
        [enabled, setIntegration],
    );

    const [columnsError, setColumnsError] = useState<any>(null);

    const defaultEntraIdGroups: IDropdownOption[] = useRef([]).current;

    const { data: entraIdGroups = defaultEntraIdGroups } = useQuery({
        queryKey: [INTEGRATIONS_MICROSOFT_ENTRAID_GROUPS],
        queryFn: () => ApiCalls.getAdGroups().then(groups => groups.data.map(group => ({ key: group.id, text: group.name } as IDropdownOption))),
        // suspense: true,
    });

    const dataverseResourcesHandle = useRef<IntegrationCardHandle | null>(null);
    const tooltipTriggerId = useId('tooltipTrigger');
    return (
        <Stack horizontal tokens={{ childrenGap: 10 }} {...disableAllSpellchecksAndAutoCompleteProp}>
            <Stack styles={{ root: { paddingLeft: 20, paddingBottom: 20, width: 250 } }}>
                <div style={{ position: 'relative' }}>
                    <Toggle disabled={isLoading || isAnyDataverseResourceEnabled} checked={enabled} onChange={toggleEnableEvent} label="Enabled" />
                    {isAnyDataverseResourceEnabled && (
                        <TooltipHost
                            content="A Dataverse integration is set up with Dataverse Resources. You must disable the Dataverse Resource event there, in order to enable this."
                            calloutProps={{ directionalHint: DirectionalHint.topCenter, target: `#${tooltipTriggerId}`, calloutMaxWidth: 200 }}
                        >
                            <IconButton iconProps={{ iconName: 'Info' }} id={tooltipTriggerId} styles={iconButtonStyle} />
                        </TooltipHost>
                    )}
                </div>
                <FilterDropdown
                    options={entraIdGroups}
                    selectedKey={entraIdGroups?.find(group => group.text === groupName)?.key || undefined}
                    onChange={(e, option) => {
                        setIntegration(
                            produce(integration => {
                                integration.configuration.groupName = option?.text || '';
                            }),
                        );
                    }}
                    disabled={entraIdGroups?.length < 1}
                    placeholder={entraIdGroups?.length < 1 ? 'Loading...' : 'Select Entra ID Group'}
                />
                <Toggle
                    disabled={isLoading}
                    checked={includeManager}
                    onChange={(e, val) =>
                        setIntegration(
                            produce(integration => {
                                integration.configuration.includeManager = val;
                            }),
                        )
                    }
                    label="Include manager"
                />
            </Stack>

            <div style={{ width: '1px', backgroundColor: 'rgba(0,0,0,0.1)', flexGrow: 1 }} />

            <Stack styles={{ root: { width: '15vw', maxWidth: 1500, minHeight: 500, paddingRight: 20 } }}>
                <Stack styles={{ root: { position: 'relative', height: '100%', width: '100%' } }}>
                    <ScrollablePane>
                        <Stack styles={{ root: { gap: 10, flexWrap: 'flexGrow' } }} horizontal verticalAlign="start">
                            <IntegrationCard
                                toggleDisabled={!(enabled && groupName !== '') || isAnyDataverseResourceEnabled}
                                eventType={'ResourcesOutbound'}
                                enabled={syncUsersToDataverse}
                                toggleEvent={(eventType: IntegrationEventType) => {
                                    setIntegration(
                                        produce(integration => {
                                            integration.configuration.syncUsersToDataverse = !integration.configuration.syncUsersToDataverse;
                                        }),
                                    );
                                }}
                                tooltip="Sync users to Dataverse"
                                ref={dataverseResourcesHandle}
                            >
                                <DataverseResourcesPane integration={integration} setIntegration={setIntegration} />
                            </IntegrationCard>
                        </Stack>
                    </ScrollablePane>
                </Stack>
                <Dialog hidden={!columnsError} dialogContentProps={{ title: 'An error occured' }}>
                    Couldn't communicate with Microsoft Entra ID. It's most likely an access issue with your account.
                    <DialogFooter>
                        <PrimaryButton onClick={() => setColumnsError(null)}>Okay</PrimaryButton>
                    </DialogFooter>
                </Dialog>
            </Stack>
        </Stack>
    );
};

const iconButtonStyle: IButtonStyles = {
    root: {
        width: 20,
        height: 20,
        position: 'absolute',
        top: 5,
        left: 60,
    },
    rootHovered: {
        backgroundColor: 'transparent',
        color: 'rgb(96, 97, 170)',
    },
    icon: {
        fontSize: 14,
    },
};
